import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge, statusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MileageInput from "@/components/MileageInput";
import { sortByUnit } from "@/lib/unitSort";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, PaperclipIcon, XIcon, PackageIcon, SparkleIcon, CaretDownIcon, UploadSimpleIcon, StorefrontIcon, WrenchIcon, ArrowUpIcon, ArrowDownIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import AiInvoiceDrop from "@/components/AiInvoiceDrop";
import AiRepairReview from "@/components/AiRepairReview";
import AiPartsReview from "@/components/AiPartsReview";

const empty = {
  truck_id: "",
  trailer_id: "",
  asset_kind: "truck",
  title: "",
  category: "major",
  description: "",
  mechanic: "",
  parts_cost: 0,
  labor_cost: 0,
  mileage_at_repair: "",
  engine_hours_at_repair: "",
  repair_date: new Date().toISOString().slice(0, 10),
  status: "completed",
  attachments: [],
  parts_used: [],
  parts_purchased: [],
  repair_type: "in_house",
  shop_id: "",
  shop_bill: 0,
  shop_invoice_number: "",
  work_performed: "",
  currency: "CAD",
};

const CATEGORIES = ["major", "engine", "brakes", "transmission", "electrical", "bodywork", "tire", "reefer", "oil_change", "pm_inspection", "annual_safety", "other"];

export default function Repairs() {
  const [repairs, setRepairs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [parts, setParts] = useState([]);
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [partPick, setPartPick] = useState("");
  const [aiParsed, setAiParsed] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [viewing, setViewing] = useState(null);  // full-detail view
  const [aiAutofilling, setAiAutofilling] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");  // 'all' | 'in_house' | 'outside'
  const [shopFilter, setShopFilter] = useState("all");  // 'all' | shopId
  const [sortDir, setSortDir] = useState("desc");       // sort direction desc | asc
  const [sortBy, setSortBy] = useState("date");         // date | cost
  // Invoice chooser flow
  const [pendingFile, setPendingFile] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [routing, setRouting] = useState(false);
  const [partsParsed, setPartsParsed] = useState(null);
  const [partsOpen, setPartsOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fxRate, setFxRate] = useState(null);  // BoC USD→CAD (e.g. 1.42)
  const [fxDate, setFxDate] = useState("");
  const invoiceInputRef = React.useRef(null);
  const [suggestions, setSuggestions] = useState([]);  // parts previously bought for the selected unit

  const filteredRepairs = repairs.filter((r) => {
    const d = (r.repair_date || "").slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    if (typeFilter !== "all" && r.repair_type !== typeFilter) return false;
    if (shopFilter !== "all" && r.shop_id !== shopFilter) return false;
    return true;
  });
  const sortedRepairs = filteredRepairs.slice().sort((a, b) => {
    let c;
    if (sortBy === "cost") c = Number(a.total_cost || 0) - Number(b.total_cost || 0);
    else c = (a.repair_date || "").localeCompare(b.repair_date || "");
    return sortDir === "asc" ? c : -c;
  });
  const _cur = (r) => (r.currency === "USD" ? "USD" : "CAD");
  const cadTotal = filteredRepairs.filter((r) => _cur(r) === "CAD").reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const usdTotal = filteredRepairs.filter((r) => _cur(r) === "USD").reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const inHouseTotal = filteredRepairs.filter((r) => r.repair_type === "in_house").reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const outsideTotal = filteredRepairs.filter((r) => r.repair_type === "outside").reduce((s, r) => s + Number(r.total_cost || 0), 0);

  const load = async () => {
    const [r, t, p, s, tr] = await Promise.all([api.get("/repairs"), api.get("/trucks"), api.get("/parts"), api.get("/repair-shops"), api.get("/trailers")]);
    setRepairs(r.data);
    setTrucks(t.data);
    setParts(p.data);
    setShops(s.data);
    setTrailers(tr.data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (e?.detail?.entity === "repairs") load(); };
    window.addEventListener("rf-data-change", h);
    return () => window.removeEventListener("rf-data-change", h);
  }, []);
  useEffect(() => {
    api.get("/fx/usd-cad").then(({ data }) => {
      if (data?.rate) { setFxRate(Number(data.rate)); setFxDate(data.date || ""); }
    }).catch(() => {});
  }, []);

  // Load part-purchase suggestions for the currently-selected unit (in-house form only)
  useEffect(() => {
    if (!open || form.repair_type !== "in_house") { setSuggestions([]); return; }
    const uid = form.asset_kind === "truck" ? form.truck_id : form.trailer_id;
    const utype = form.asset_kind;
    if (!uid) { setSuggestions([]); return; }
    api.get(`/parts/suggestions?unit_id=${uid}&unit_type=${utype}`)
      .then(({ data }) => setSuggestions(data || []))
      .catch(() => setSuggestions([]));
  }, [open, form.truck_id, form.trailer_id, form.asset_kind, form.repair_type]);

  const truckLabel = (id) => {
    const t = trucks.find((x) => x.id === id);
    return t ? `${t.unit_number} · ${t.make} ${t.model}` : "—";
  };

  const trailerLabel = (id) => {
    const t = trailers.find((x) => x.id === id);
    return t ? `${t.unit_number} · ${t.trailer_type.replace(/_/g, " ")} · ${t.make}` : "—";
  };

  const assetLabel = (r) => r.trailer_id ? trailerLabel(r.trailer_id) : truckLabel(r.truck_id);

  // ---- Invoice chooser flow ----
  const handleInvoiceFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported");
      return;
    }
    setPendingFile(file);
    setChooserOpen(true);
  };

  const routeInvoice = async (kind) => {
    // kind: 'parts' | 'outside' | 'in_house'
    if (!pendingFile) return;
    setRouting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      let url = "";
      if (kind === "parts") url = "/parts/parse-invoice";
      else url = `/repairs/parse-invoice?repair_type=${kind}`;
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      // Also persist the original invoice so the Parts Purchased panel can
      // surface it later on click. Best-effort — never blocks the parse flow.
      let uploaded = null;
      if (kind === "parts") {
        try {
          const upFd = new FormData();
          upFd.append("file", pendingFile);
          const up = await api.post("/upload", upFd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          uploaded = {
            invoice_file_id: up.data.id,
            invoice_file_name: up.data.name,
            invoice_file_type: up.data.type,
          };
        } catch { /* non-fatal */ }
      }
      setChooserOpen(false);
      setPendingFile(null);
      if (kind === "parts") {
        setPartsParsed({ ...data, ...(uploaded || {}) });
        setPartsOpen(true);
        toast.success("AI extracted parts — review before saving to inventory");
      } else {
        setAiParsed(data);
        setAiOpen(true);
        toast.success("AI extraction complete — please review");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Extraction failed");
    } finally {
      setRouting(false);
    }
  };

  const openAdd = () => { setEditing(null); setForm({ ...empty, truck_id: trucks[0]?.id || "", asset_kind: "truck" }); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...empty,
      ...r,
      repair_date: r.repair_date?.slice(0, 10),
      attachments: r.attachments || [],
      parts_used: r.parts_used || [],
      parts_purchased: [],
      asset_kind: r.trailer_id ? "trailer" : "truck",
      truck_id: r.truck_id || "",
      trailer_id: r.trailer_id || "",
    });
    setOpen(true);
  };

  const addPartLine = () => {
    if (!partPick) return;
    const part = parts.find((p) => p.id === partPick);
    if (!part) return;
    // available considering current edited repair (we restore old qty later)
    const original_qty = editing?.parts_used?.find((l) => l.part_id === part.id)?.quantity || 0;
    const already_in_form = form.parts_used.find((l) => l.part_id === part.id)?.quantity || 0;
    const available = part.quantity_in_stock + original_qty - already_in_form;
    if (available <= 0) {
      toast.error(`No more "${part.name}" in stock`);
      return;
    }
    setForm((f) => {
      const existing = f.parts_used.find((l) => l.part_id === part.id);
      let next;
      if (existing) {
        next = f.parts_used.map((l) => l.part_id === part.id ? { ...l, quantity: l.quantity + 1, line_total: (l.quantity + 1) * l.unit_cost } : l);
      } else {
        next = [...f.parts_used, { part_id: part.id, name: part.name, part_number: part.part_number || "", quantity: 1, unit_cost: part.unit_cost, line_total: part.unit_cost }];
      }
      const sum = next.reduce((s, l) => s + (l.quantity * l.unit_cost), 0);
      return { ...f, parts_used: next, parts_cost: Math.round(sum * 100) / 100 };
    });
    setPartPick("");
  };

  // Add a suggested part (previously purchased for this unit). Tags line with allocation_id.
  const addSuggestion = (sug) => {
    const desired = Number(sug.qty_remaining) || 0;
    const stockAvail = Number(sug.current_stock) || 0;
    if (desired <= 0) {
      toast.error("This allocation is already fully used");
      return;
    }
    const takeFromAlloc = Math.min(desired, stockAvail);
    if (takeFromAlloc === 0) {
      toast.error(`"${sug.part_name}" was reserved for this unit but 0 are in stock right now — restock first`);
      return;
    }
    if (takeFromAlloc < desired) {
      toast.warning(`Only ${takeFromAlloc} of ${desired} reserved for this unit are in stock — added ${takeFromAlloc}`);
    } else if (desired < Number(sug.qty_purchased)) {
      toast.info(`${desired} of ${sug.qty_purchased} originally purchased for this unit still available`);
    }
    setForm((f) => {
      const existing = f.parts_used.find((l) => l.part_id === sug.part_id);
      let next;
      if (existing) {
        next = f.parts_used.map((l) => l.part_id === sug.part_id ? { ...l, quantity: l.quantity + takeFromAlloc, allocation_id: existing.allocation_id || sug.id, line_total: (l.quantity + takeFromAlloc) * l.unit_cost } : l);
      } else {
        const uc = Number(sug.unit_cost) || Number(sug.part_current_cost) || 0;
        next = [...f.parts_used, { part_id: sug.part_id, name: sug.part_name, part_number: sug.part_number || "", quantity: takeFromAlloc, unit_cost: uc, line_total: uc * takeFromAlloc, allocation_id: sug.id }];
      }
      const sum = next.reduce((s, l) => s + (l.quantity * l.unit_cost), 0);
      return { ...f, parts_used: next, parts_cost: Math.round(sum * 100) / 100 };
    });
  };

  const changePartQty = (part_id, qty) => {
    qty = Math.max(0, Number(qty) || 0);
    setForm((f) => {
      const next = f.parts_used
        .map((l) => l.part_id === part_id ? { ...l, quantity: qty, line_total: qty * l.unit_cost } : l)
        .filter((l) => l.quantity > 0);
      const sum = next.reduce((s, l) => s + (l.quantity * l.unit_cost), 0);
      return { ...f, parts_used: next, parts_cost: Math.round(sum * 100) / 100 };
    });
  };

  const removePartLine = (part_id) => {
    setForm((f) => {
      const next = f.parts_used.filter((l) => l.part_id !== part_id);
      const sum = next.reduce((s, l) => s + (l.quantity * l.unit_cost), 0);
      return { ...f, parts_used: next, parts_cost: Math.round(sum * 100) / 100 };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        truck_id: form.asset_kind === "truck" ? (form.truck_id || null) : null,
        trailer_id: form.asset_kind === "trailer" ? (form.trailer_id || null) : null,
        parts_cost: Number(form.parts_cost) || 0,
        labor_cost: Number(form.labor_cost) || 0,
        shop_bill: Number(form.shop_bill) || 0,
        mileage_at_repair: form.mileage_at_repair ? Number(form.mileage_at_repair) : null,
        engine_hours_at_repair: form.engine_hours_at_repair ? Number(form.engine_hours_at_repair) : null,
        parts_used: form.parts_used || [],
        parts_purchased: form.parts_purchased || [],
        shop_id: form.shop_id || null,
      };
      delete body.asset_kind;
      if (editing) {
        await api.put(`/repairs/${editing.id}`, body);
        toast.success("Repair updated");
      } else {
        await api.post("/repairs", body);
        toast.success("Repair logged");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this repair record?")) return;
    await api.delete(`/repairs/${r.id}`);
    toast.success("Deleted");
    await load();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), data] }));
      toast.success("Attached " + data.name);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="repairs-page">
      <PageHeader eyebrow="Repair logs" title="Repairs" subtitle="Track every repair — parts, labor, mechanic notes, and receipts.">
        <Button onClick={openAdd} disabled={trucks.length === 0 && trailers.length === 0} data-testid="add-repair-button" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Log Repair
        </Button>
      </PageHeader>

      <div className="p-8">
        {/* AI Invoice Upload — unified: asks whether parts or repair */}
        <div className="mb-6">
          <div
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleInvoiceFile(f);
            }}
            onClick={() => invoiceInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-all ${
              dragActive ? "border-blue-500 bg-blue-50 scale-[1.005]" : "border-slate-300 bg-white hover:border-blue-500 hover:bg-blue-50/40"
            }`}
            data-testid="unified-invoice-drop"
          >
            <input ref={invoiceInputRef} type="file" accept="application/pdf" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleInvoiceFile(f); }}
              data-testid="unified-invoice-input"
            />
            <div className="flex flex-col items-center gap-2 pointer-events-none">
              <div className={`w-12 h-12 flex items-center justify-center rounded-sm ${dragActive ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"}`}>
                <UploadSimpleIcon size={26} weight="bold" />
              </div>
              <div className="font-display font-bold text-slate-900 text-lg">
                {dragActive ? "Drop it right here" : "Upload an invoice PDF"}
              </div>
              <div className="text-xs text-slate-500 max-w-md">
                We&apos;ll ask if it&apos;s a <b>parts purchase</b> (goes to inventory) or a <b>repair invoice</b> (creates a repair log — outside shop or in-house).
              </div>
            </div>
          </div>
        </div>
        {/* DATE FILTER */}
        <div className="mb-4 bg-white border border-slate-300 rounded-sm p-4 flex flex-wrap items-end gap-3" data-testid="repairs-date-filter">
          <div>
            <label className="text-[10px] uppercase font-mono text-slate-500 font-bold">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="repairs-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-mono text-slate-500 font-bold">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="repairs-to" />
          </div>
          <button
            type="button"
            onClick={() => { const t = new Date().toISOString().slice(0, 10); setDateFrom(t); setDateTo(t); }}
            className="px-3 py-2 border border-slate-300 hover:border-blue-500 rounded-sm text-xs font-mono uppercase font-bold text-slate-700 hover:text-blue-700"
            data-testid="repairs-today"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => { const d = new Date(); const w = new Date(d.getTime() - 6 * 86400000); setDateFrom(w.toISOString().slice(0, 10)); setDateTo(d.toISOString().slice(0, 10)); }}
            className="px-3 py-2 border border-slate-300 hover:border-blue-500 rounded-sm text-xs font-mono uppercase font-bold text-slate-700 hover:text-blue-700"
            data-testid="repairs-7d"
          >
            Last 7 days
          </button>
          <button
            type="button"
            onClick={() => { const d = new Date(); const m = new Date(d.getFullYear(), d.getMonth(), 1); setDateFrom(m.toISOString().slice(0, 10)); setDateTo(d.toISOString().slice(0, 10)); }}
            className="px-3 py-2 border border-slate-300 hover:border-blue-500 rounded-sm text-xs font-mono uppercase font-bold text-slate-700 hover:text-blue-700"
            data-testid="repairs-month"
          >
            This month
          </button>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="px-3 py-2 rounded-sm text-xs font-mono uppercase font-bold text-red-600 hover:text-red-800"
              data-testid="repairs-clear"
            >
              Clear
            </button>
          )}
          <div className="ml-auto text-right">
            <div className="text-[10px] font-mono uppercase text-slate-500">Showing</div>
            <div className="font-display text-lg font-black tabular-nums">{filteredRepairs.length}<span className="text-xs text-slate-500 font-mono ml-2">of {repairs.length}</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase text-slate-500">Total CAD</div>
            <div className="font-display text-lg font-black tabular-nums text-slate-800" data-testid="repairs-range-cad">CA${Math.round(cadTotal).toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase text-slate-500">Total USD</div>
            <div className="font-display text-lg font-black tabular-nums text-emerald-700" data-testid="repairs-range-usd">US${Math.round(usdTotal).toLocaleString()}</div>
            {fxRate && usdTotal > 0 && (
              <div className="text-[10px] font-mono text-slate-500" data-testid="repairs-range-usd-in-cad" title={`Bank of Canada USD→CAD ${fxRate.toFixed(4)} · ${fxDate}`}>
                ≈ CA${Math.round(usdTotal * fxRate).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* TYPE + SHOP FILTER STRIP */}
        <div className="mb-4 bg-white border border-slate-300 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="repairs-type-filter">
          <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Type</div>
          {[
            { k: "all", label: "All", tid: "type-all" },
            { k: "in_house", label: "In-house", tid: "type-in-house" },
            { k: "outside", label: "Outside shop", tid: "type-outside" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTypeFilter(t.k)}
              data-testid={`repairs-${t.tid}`}
              className={`px-3 py-1.5 border-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors ${
                typeFilter === t.k ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:bg-slate-50 text-slate-600"
              }`}
            >{t.label}</button>
          ))}

          <div className="ml-4 text-[10px] uppercase font-mono text-slate-500 font-bold">Shop</div>
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger className="rounded-sm w-56" data-testid="repairs-shop-filter"><SelectValue placeholder="All shops" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All shops</SelectItem>
              {shops.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {(typeFilter !== "all" || shopFilter !== "all") && (
            <button
              type="button"
              onClick={() => { setTypeFilter("all"); setShopFilter("all"); }}
              className="px-3 py-1.5 rounded-sm text-xs font-mono uppercase font-bold text-red-600 hover:text-red-800"
              data-testid="repairs-clear-filters"
            >Clear filters</button>
          )}

          <div className="ml-4 text-[10px] uppercase font-mono text-slate-500 font-bold">Sort</div>
          <button
            type="button"
            onClick={() => { if (sortBy === "date") setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy("date"); setSortDir("desc"); } }}
            className={`flex items-center gap-1 px-3 py-1.5 border-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors ${sortBy === "date" ? "border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
            data-testid="repairs-sort-date"
            title="Sort by repair date"
          >
            {sortBy === "date" && sortDir === "asc" ? <ArrowUpIcon size={12} weight="bold" /> : <ArrowDownIcon size={12} weight="bold" />}
            Date
          </button>
          <button
            type="button"
            onClick={() => { if (sortBy === "cost") setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy("cost"); setSortDir("desc"); } }}
            className={`flex items-center gap-1 px-3 py-1.5 border-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors ${sortBy === "cost" ? "border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
            data-testid="repairs-sort-cost"
            title="Sort by total cost"
          >
            {sortBy === "cost" && sortDir === "asc" ? <ArrowUpIcon size={12} weight="bold" /> : <ArrowDownIcon size={12} weight="bold" />}
            Cost
          </button>

          {/* Split totals: CAD + USD (auto-detected from invoice shop address) + type breakdown */}
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right border-l border-slate-200 pl-4">
              <div className="text-[10px] font-mono uppercase text-slate-500">Total CAD</div>
              <div className="font-display text-base font-black tabular-nums text-slate-800" data-testid="repairs-total-cad">CA${Math.round(cadTotal).toLocaleString()}</div>
            </div>
            <div className="text-right border-l border-slate-200 pl-4">
              <div className="text-[10px] font-mono uppercase text-slate-500">Total USD</div>
              <div className="font-display text-base font-black tabular-nums text-emerald-700" data-testid="repairs-total-usd">US${Math.round(usdTotal).toLocaleString()}</div>
              {fxRate && usdTotal > 0 && (
                <div className="text-[10px] font-mono text-slate-500" data-testid="repairs-total-usd-in-cad" title={`Bank of Canada USD→CAD ${fxRate.toFixed(4)} · ${fxDate}`}>
                  ≈ CA${Math.round(usdTotal * fxRate).toLocaleString()}
                </div>
              )}
            </div>
            <div className="text-right border-l border-slate-200 pl-4">
              <div className="text-[10px] font-mono uppercase text-slate-500">In-house $</div>
              <div className="font-display text-base font-black tabular-nums text-slate-700" data-testid="repairs-in-house-total">${Math.round(inHouseTotal).toLocaleString()}</div>
            </div>
            <div className="text-right border-l border-slate-200 pl-4">
              <div className="text-[10px] font-mono uppercase text-slate-500">Outside shop $</div>
              <div className="font-display text-base font-black tabular-nums text-amber-700" data-testid="repairs-outside-total">${Math.round(outsideTotal).toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* SHOP DETAIL PANEL — when a specific shop is picked */}
        {shopFilter !== "all" && (() => {
          const shop = shops.find((s) => s.id === shopFilter);
          if (!shop) return null;
          const shopRepairs = filteredRepairs;  // already filtered by shop
          const shopCad = shopRepairs.filter((r) => _cur(r) === "CAD").reduce((s, r) => s + Number(r.total_cost || 0), 0);
          const shopUsd = shopRepairs.filter((r) => _cur(r) === "USD").reduce((s, r) => s + Number(r.total_cost || 0), 0);
          return (
            <div className="mb-4 bg-blue-50 border-2 border-blue-200 rounded-sm p-4" data-testid="shop-summary-panel">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] uppercase font-mono text-blue-700 font-bold">Shop summary</div>
                  <div className="font-display text-xl font-black tracking-tight text-slate-900 mt-0.5">{shop.name}</div>
                  {(shop.address || shop.phone) && (
                    <div className="text-xs text-slate-600 mt-1 font-mono">
                      {shop.address}{shop.address && shop.phone ? " · " : ""}{shop.phone}
                    </div>
                  )}
                </div>
                <div className="flex gap-6">
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase text-slate-500">Repairs done here</div>
                    <div className="font-display text-2xl font-black tabular-nums">{shopRepairs.length}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase text-slate-500">Total CAD</div>
                    <div className="font-display text-2xl font-black tabular-nums text-slate-800" data-testid="shop-total-cad">CA${Math.round(shopCad).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase text-slate-500">Total USD</div>
                    <div className="font-display text-2xl font-black tabular-nums text-emerald-700" data-testid="shop-total-usd">US${Math.round(shopUsd).toLocaleString()}</div>
                    {fxRate && shopUsd > 0 && (
                      <div className="text-[10px] font-mono text-slate-500" title={`Bank of Canada USD→CAD ${fxRate.toFixed(4)} · ${fxDate}`}>
                        ≈ CA${Math.round(shopUsd * fxRate).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        <div className="bg-white border border-slate-300 rounded-sm">
          {filteredRepairs.length === 0 ? (
            <div className="p-12 text-center">
              <div className="font-mono text-xs text-slate-400 mb-2">/// NO REPAIRS {(dateFrom || dateTo) ? "IN THIS DATE RANGE" : "LOGGED"}</div>
              <div className="text-sm text-slate-600">{(dateFrom || dateTo) ? "Try a wider range or clear the filter." : "Start logging your fleet&apos;s repair history."}</div>
            </div>
          ) : (
            <table className="fleet-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Asset</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Shop / Mechanic</th>
                  <th>Parts</th>
                  <th>Bill</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedRepairs.map((r) => {
                  const sb = statusBadge(r.status);
                  const shop = shops.find((s) => s.id === r.shop_id);
                  const isOutside = r.repair_type === "outside";
                  return (
                    <tr
                      key={r.id}
                      data-testid={`repair-list-${r.id}`}
                      onClick={() => setViewing(r)}
                      className="cursor-pointer hover:bg-blue-50/60 transition-colors"
                    >
                      <td className="font-mono">{r.repair_date?.slice(0, 10)}</td>
                      <td className="font-mono font-semibold">{assetLabel(r)}</td>
                      <td>
                        <Badge tone={isOutside ? "warning" : "info"}>{isOutside ? "Outside" : "In-house"}</Badge>
                      </td>
                      <td className="font-semibold">{r.title}<div className="text-[10px] text-slate-500 capitalize font-mono">{r.category}</div></td>
                      <td className="text-xs">{isOutside ? (shop?.name || "—") : (r.mechanic || "—")}</td>
                      <td className="font-mono tabular-nums">${Number(r.parts_cost).toLocaleString()}</td>
                      <td className="font-mono tabular-nums">{isOutside ? `$${Number(r.shop_bill || 0).toLocaleString()}` : "—"}</td>
                      <td className="font-mono tabular-nums font-bold">
                        <span className={`text-[10px] font-mono font-bold mr-1 ${_cur(r) === "USD" ? "text-emerald-700" : "text-slate-500"}`} data-testid={`repair-cur-${r.id}`}>{_cur(r) === "USD" ? "US$" : "CA$"}</span>
                        {Number(r.total_cost).toLocaleString()}
                      </td>
                      <td><Badge tone={sb.tone}>{sb.label}</Badge></td>
                      <td className="space-x-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-semibold" data-testid={`edit-repair-${r.id}`}>Edit</button>
                        <button onClick={() => del(r)} className="text-red-600 hover:underline text-xs font-semibold" data-testid={`del-repair-${r.id}`}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="repair-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit repair" : "Log new repair"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-2 block">Asset *</Label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, asset_kind: "truck", trailer_id: "" })}
                  data-testid="asset-kind-truck"
                  className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "truck" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  Truck
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, asset_kind: "trailer", truck_id: "" })}
                  data-testid="asset-kind-trailer"
                  className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "trailer" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  Trailer
                </button>
              </div>
              {form.asset_kind === "truck" ? (
                <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="repair-truck"><SelectValue placeholder="Select truck" /></SelectTrigger>
                  <SelectContent>
                    {sortByUnit(trucks).map((t) => <SelectItem key={t.id} value={t.id}>{t.unit_number} · {t.make} {t.model}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={form.trailer_id} onValueChange={(v) => setForm({ ...form, trailer_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="repair-trailer"><SelectValue placeholder="Select trailer" /></SelectTrigger>
                  <SelectContent>
                    {sortByUnit(trailers).map((t) => <SelectItem key={t.id} value={t.id}>{t.unit_number} · {t.trailer_type.replace(/_/g, " ")} · {t.make}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5 rounded-sm" data-testid="repair-title" placeholder="e.g. Replaced front brake pads" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="repair-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="repair-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Repair Date *</Label>
              <Input type="date" value={form.repair_date} onChange={(e) => setForm({ ...form, repair_date: e.target.value })} required className="mt-1.5 rounded-sm" data-testid="repair-date" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">{form.asset_kind === "trailer" ? "Engine hours at repair" : "Mileage at repair"}</Label>
              {form.asset_kind === "trailer" ? (
                <Input type="number" step="0.1" value={form.engine_hours_at_repair} onChange={(e) => setForm({ ...form, engine_hours_at_repair: e.target.value })} className="mt-1.5 rounded-sm" data-testid="repair-engine-hours" />
              ) : (
                <div className="mt-1.5">
                  <MileageInput value={form.mileage_at_repair} onChange={(v) => setForm({ ...form, mileage_at_repair: v })} data-testid="repair-mileage" />
                </div>
              )}
            </div>

            {/* In-house / Outside toggle */}
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-2 block">Repair type *</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, repair_type: "in_house" })}
                  data-testid="repair-type-in-house"
                  className={`px-4 py-3 border-2 rounded-sm text-left transition-colors duration-150 ${form.repair_type === "in_house" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  <div className="font-display font-bold text-sm">In-house</div>
                  <div className="text-xs text-slate-600 mt-0.5">Parts only — no labor charge</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, repair_type: "outside" })}
                  data-testid="repair-type-outside"
                  className={`px-4 py-3 border-2 rounded-sm text-left transition-colors duration-150 ${form.repair_type === "outside" ? "border-amber-600 bg-amber-50" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  <div className="font-display font-bold text-sm">Outside shop</div>
                  <div className="text-xs text-slate-600 mt-0.5">Shop bill — no inventory use</div>
                </button>
              </div>
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Mechanic / Technician</Label>
              <Input value={form.mechanic} onChange={(e) => setForm({ ...form, mechanic: e.target.value })} className="mt-1.5 rounded-sm" data-testid="repair-mechanic" placeholder={form.repair_type === "outside" ? "Shop technician name" : "In-house mechanic name"} />
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Description / Notes</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-sm min-h-[80px]" data-testid="repair-desc" />
            </div>

            <div className="col-span-2 border border-slate-200 rounded-sm p-4 bg-slate-50" style={{ display: form.repair_type === "in_house" ? "block" : "none" }}>
              {/* Suggested parts previously bought for this specific unit */}
              {suggestions.length > 0 && (
                <div className="mb-4 border border-emerald-300 rounded-sm bg-emerald-50 p-3" data-testid="suggested-parts-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <SparkleIcon size={14} weight="fill" className="text-emerald-700" />
                    <Label className="text-xs uppercase tracking-wider font-bold text-emerald-900">
                      Suggested for this unit — bought on prior invoices
                    </Label>
                    <span className="ml-auto font-mono text-[10px] text-emerald-800">{suggestions.length} reserved</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((s) => {
                      const already = form.parts_used.find((l) => l.allocation_id === s.id);
                      const disabled = !!already || s.qty_remaining <= 0;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => addSuggestion(s)}
                          disabled={disabled}
                          data-testid={`suggest-add-${s.id}`}
                          className={`text-left border rounded-sm px-2 py-1.5 text-xs transition-colors ${disabled ? "border-slate-200 bg-white text-slate-400 cursor-not-allowed" : "border-emerald-400 bg-white hover:bg-emerald-100 hover:border-emerald-600"}`}
                          title={`${s.qty_remaining} of ${s.qty_purchased} reserved · ${s.current_stock} in stock · Inv #${s.invoice_number || "—"}`}
                        >
                          <div className="font-semibold text-slate-900 flex items-center gap-1">
                            <PlusIcon size={10} weight="bold" className="text-emerald-700" /> {s.part_name}
                          </div>
                          <div className="font-mono text-[10px] text-slate-600 mt-0.5">
                            {s.part_number ? `${s.part_number} · ` : ""}{s.qty_remaining} reserved · {s.current_stock} in stock
                          </div>
                          {s.qty_remaining < s.qty_purchased && (
                            <div className="font-mono text-[9px] text-amber-700 mt-0.5">
                              {s.qty_purchased - s.qty_remaining} already used
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[10px] text-emerald-800 font-mono">
                    💡 Click to add to this repair. Stock will deduct as usual; the reservation for this unit will also decrease.
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 flex items-center gap-2">
                  <PackageIcon size={14} weight="bold" /> Parts used from inventory
                </Label>
                <span className="font-mono text-xs text-slate-500">
                  Total: <span className="font-bold text-slate-900">${Number(form.parts_cost).toFixed(2)}</span>
                </span>
              </div>

              <div className="flex gap-2 mb-3">
                <Select value={partPick} onValueChange={setPartPick}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="repair-part-select">
                    <SelectValue placeholder={parts.length === 0 ? "No parts in inventory — add some first" : "Select part to add"} />
                  </SelectTrigger>
                  <SelectContent>
                    {parts.map((p) => {
                      const original_qty = editing?.parts_used?.find((l) => l.part_id === p.id)?.quantity || 0;
                      const used_in_form = form.parts_used.find((l) => l.part_id === p.id)?.quantity || 0;
                      const available = p.quantity_in_stock + original_qty - used_in_form;
                      return (
                        <SelectItem key={p.id} value={p.id} disabled={available <= 0}>
                          {p.name} {p.part_number ? `(${p.part_number})` : ""} — {available} in stock · ${Number(p.unit_cost).toFixed(2)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={addPartLine} disabled={!partPick} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-part-to-repair">
                  <PlusIcon size={14} weight="bold" />
                </Button>
              </div>

              {form.parts_used.length === 0 ? (
                <div className="text-xs text-slate-500 font-mono text-center py-3 border border-dashed border-slate-300 rounded-sm">
                  No parts added. Stock will not change unless parts are selected.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      <th className="text-left py-1">Part</th>
                      <th className="text-right py-1">Qty</th>
                      <th className="text-right py-1">Unit $</th>
                      <th className="text-right py-1">Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.parts_used.map((l) => (
                      <tr key={l.part_id} className="border-t border-slate-200" data-testid={`repair-part-line-${l.part_id}`}>
                        <td className="py-2">
                          <div className="font-semibold">{l.name}</div>
                          {l.part_number && <div className="font-mono text-[10px] text-slate-500">{l.part_number}</div>}
                        </td>
                        <td className="py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            value={l.quantity}
                            onChange={(e) => changePartQty(l.part_id, e.target.value)}
                            className="w-16 border border-slate-300 rounded-sm px-2 py-1 text-right font-mono"
                            data-testid={`part-qty-${l.part_id}`}
                          />
                        </td>
                        <td className="py-2 text-right font-mono">${Number(l.unit_cost).toFixed(2)}</td>
                        <td className="py-2 text-right font-mono font-bold">${Number(l.quantity * l.unit_cost).toFixed(2)}</td>
                        <td className="py-2 text-right">
                          <button type="button" onClick={() => removePartLine(l.part_id)} className="text-red-600" data-testid={`remove-part-${l.part_id}`}>
                            <XIcon size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Outside shop section */}
            <div className="col-span-2 border border-amber-200 rounded-sm p-4 bg-amber-50" style={{ display: form.repair_type === "outside" ? "block" : "none" }} data-testid="outside-shop-section">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-wider font-bold text-amber-900">Outside repair shop</Label>
                <label className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-400 bg-white hover:bg-amber-100 rounded-sm text-[11px] font-bold text-amber-900 cursor-pointer" data-testid="outside-autofill-btn">
                  {aiAutofilling ? <><SparkleIcon size={12} weight="fill" className="animate-pulse" /> Reading...</> : <><SparkleIcon size={12} weight="bold" /> Drop invoice to auto-fill</>}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    disabled={aiAutofilling}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setAiAutofilling(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", file);
                        const { data } = await api.post(`/repairs/parse-invoice?repair_type=outside`, fd, {
                          headers: { "Content-Type": "multipart/form-data" },
                          timeout: 120000,
                        });
                        const ex = data.extracted || {};
                        // If AI matched or auto-created a shop that isn't in our local list yet, add it
                        if (data.matched_shop && !shops.find((s) => s.id === data.matched_shop.id)) {
                          setShops((prev) => [...prev, data.matched_shop]);
                        }
                        setForm((f) => ({
                          ...f,
                          truck_id: data.matched_truck?.id || f.truck_id,
                          shop_id: data.matched_shop?.id || f.shop_id,
                          title: ex.summary || f.title,
                          category: ex.category || f.category,
                          repair_date: ex.invoice_date || f.repair_date,
                          mileage_at_repair: ex.mileage || f.mileage_at_repair,
                          shop_bill: ex.total || f.shop_bill,
                          shop_invoice_number: ex.invoice_number || f.shop_invoice_number,
                          work_performed: (ex.line_items || []).map((l) => `${l.description || ""}${l.qty ? ` × ${l.qty}` : ""}${l.amount ? ` — $${l.amount}` : ""}`).filter(Boolean).join("\n") || f.work_performed,
                          mechanic: f.mechanic,
                          description: `${f.description ? f.description + "\n\n" : ""}Shop: ${ex.shop_name || ""}\n${ex.shop_address || ""}\n${ex.shop_phone || ""}`.trim(),
                        }));
                        toast.success(`Auto-filled from ${file.name}${data.matched_truck ? " · truck matched" : ""}${data.matched_shop ? " · shop matched" : ""}`);
                      } catch (err) {
                        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI parse failed");
                      } finally {
                        setAiAutofilling(false);
                        e.target.value = "";
                      }
                    }}
                    data-testid="outside-autofill-input"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Shop *</Label>
                  <Select value={form.shop_id} onValueChange={(v) => setForm({ ...form, shop_id: v })}>
                    <SelectTrigger className="mt-1.5 rounded-sm bg-white" data-testid="repair-shop-select">
                      <SelectValue placeholder={shops.length === 0 ? "No shops added yet — add one first" : "Select shop"} />
                    </SelectTrigger>
                    <SelectContent>
                      {shops.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}{s.specialties ? ` — ${s.specialties}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Shop bill ($) *</Label>
                  <Input type="number" step="0.01" value={form.shop_bill} onChange={(e) => setForm({ ...form, shop_bill: e.target.value })} required={form.repair_type === "outside"} className="mt-1.5 rounded-sm" data-testid="repair-shop-bill" placeholder="0.00" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Currency</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, currency: "CAD" })}
                      data-testid="repair-currency-cad"
                      className={`px-2.5 py-2 border-2 rounded-sm text-xs font-bold font-mono transition-colors ${form.currency === "CAD" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-300 hover:bg-slate-50 text-slate-700"}`}
                    >CA$ CAD</button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, currency: "USD" })}
                      data-testid="repair-currency-usd"
                      className={`px-2.5 py-2 border-2 rounded-sm text-xs font-bold font-mono transition-colors ${form.currency === "USD" ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-300 hover:bg-slate-50 text-slate-700"}`}
                    >US$ USD</button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Invoice #</Label>
                  <Input value={form.shop_invoice_number} onChange={(e) => setForm({ ...form, shop_invoice_number: e.target.value })} className="mt-1.5 rounded-sm font-mono" data-testid="repair-shop-invoice" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Work performed by shop</Label>
                  <Textarea value={form.work_performed} onChange={(e) => setForm({ ...form, work_performed: e.target.value })} placeholder="e.g. Replaced turbo, rebuilt fuel injection pump" className="mt-1.5 rounded-sm" data-testid="repair-work-performed" />
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-xs font-mono uppercase tracking-wider text-slate-600 hover:text-slate-900 font-bold inline-flex items-center gap-1"
                data-testid="toggle-advanced"
              >
                <CaretDownIcon size={12} weight="bold" className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                {showAdvanced ? "Hide" : "Show"} advanced (attachments, mileage details)
              </button>
            </div>

            <div className="col-span-2" style={{ display: showAdvanced ? "block" : "none" }}>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">
                {form.repair_type === "outside" ? "Shop invoice (PDF) & photos" : "Receipts / photos"}
              </Label>
              <div className="mt-1.5 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-sm text-sm cursor-pointer hover:bg-slate-50">
                  <PaperclipIcon size={14} /> {uploading ? "Uploading..." : form.repair_type === "outside" ? "Attach shop invoice" : "Attach file"}
                  <input type="file" onChange={onUpload} className="hidden" accept="image/*,application/pdf" data-testid="repair-upload" />
                </label>
                <span className="text-xs text-slate-500 font-mono">
                  {form.repair_type === "outside" ? "Attach the PDF invoice from the shop · max 5MB" : "PNG, JPG, PDF · max 5MB"}
                </span>
              </div>
              {form.attachments?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {form.attachments.map((a, i) => (
                    <div key={`${a.name}-${a.size}-${i}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 px-2 py-1.5 rounded-sm text-xs">
                      <a
                        href={a.data_base64 ? `data:${a.type || "application/octet-stream"};base64,${a.data_base64}` : "#"}
                        download={a.name}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono truncate text-blue-700 hover:underline flex-1"
                        data-testid={`attachment-${i}`}
                      >
                        {a.name} <span className="text-slate-400">({Math.round((a.size || 0) / 1024)} KB)</span>
                      </a>
                      <button type="button" onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, idx) => idx !== i) }))} className="text-red-600 ml-2" data-testid={`remove-attachment-${i}`}>
                        <XIcon size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="repair-save">{editing ? "Update" : "Log repair"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AiRepairReview
        open={aiOpen}
        onOpenChange={setAiOpen}
        parsed={aiParsed}
        trucks={trucks}
        trailers={trailers}
        shops={shops}
        onSaved={() => { setAiParsed(null); load(); }}
      />

      <AiPartsReview
        open={partsOpen}
        onOpenChange={setPartsOpen}
        parsed={partsParsed}
        onSaved={() => { setPartsParsed(null); load(); }}
      />

      {/* INVOICE TYPE CHOOSER */}
      <Dialog open={chooserOpen} onOpenChange={(v) => { if (!v && !routing) { setChooserOpen(false); setPendingFile(null); } }}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="invoice-chooser-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">What kind of invoice is this?</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2 mb-2 font-mono">
            {pendingFile?.name}
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <button
              type="button"
              disabled={routing}
              onClick={() => routeInvoice("parts")}
              data-testid="chooser-parts"
              className="text-left p-4 border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 rounded-sm transition-colors disabled:opacity-50"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-blue-600 text-white mb-3">
                <PackageIcon size={20} weight="bold" />
              </div>
              <div className="font-display font-black text-slate-900">Parts purchase</div>
              <div className="text-xs text-slate-500 mt-1">I bought parts. Add them to inventory (no repair record).</div>
            </button>
            <button
              type="button"
              disabled={routing}
              onClick={() => routeInvoice("outside")}
              data-testid="chooser-outside"
              className="text-left p-4 border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 rounded-sm transition-colors disabled:opacity-50"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-amber-600 text-white mb-3">
                <StorefrontIcon size={20} weight="bold" />
              </div>
              <div className="font-display font-black text-slate-900">Outside shop repair</div>
              <div className="text-xs text-slate-500 mt-1">A shop did the work. AI matches the truck & shop, creates a repair log.</div>
            </button>
            <button
              type="button"
              disabled={routing}
              onClick={() => routeInvoice("in_house")}
              data-testid="chooser-inhouse"
              className="text-left p-4 border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 rounded-sm transition-colors disabled:opacity-50"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-slate-800 text-white mb-3">
                <WrenchIcon size={20} weight="bold" />
              </div>
              <div className="font-display font-black text-slate-900">In-house work order</div>
              <div className="text-xs text-slate-500 mt-1">Our mechanic did the work. AI extracts parts & labor, creates a repair log.</div>
            </button>
          </div>
          {routing && (
            <div className="text-center text-sm text-blue-700 font-mono mt-4 flex items-center justify-center gap-2">
              <SparkleIcon size={16} weight="fill" className="animate-pulse" />
              AI is reading your PDF...
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => { setChooserOpen(false); setPendingFile(null); }} disabled={routing} className="rounded-sm">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPAIR DETAIL VIEW */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="repair-detail-dialog">
          {viewing && (() => {
            const shop = shops.find((s) => s.id === viewing.shop_id);
            const isOutside = viewing.repair_type === "outside";
            const sb = statusBadge(viewing.status);
            const partsMap = new Map(parts.map((p) => [p.id, p]));
            return (
              <div>
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight flex items-center gap-3">
                    <span>{viewing.title}</span>
                    <Badge tone={isOutside ? "warning" : "info"}>{isOutside ? "Outside" : "In-house"}</Badge>
                    <Badge tone={sb.tone}>{sb.label}</Badge>
                  </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
                  <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Date</div>
                    <div className="font-mono text-sm">{viewing.repair_date?.slice(0, 10)}</div>
                  </div>
                  <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Asset</div>
                    <div className="font-mono text-sm font-semibold">{assetLabel(viewing)}</div>
                  </div>
                  <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Category</div>
                    <div className="text-sm capitalize">{viewing.category || "—"}</div>
                  </div>
                  <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">{isOutside ? "Shop" : "Mechanic"}</div>
                    <div className="text-sm">{isOutside ? (shop?.name || "—") : (viewing.mechanic || "—")}</div>
                  </div>
                  {viewing.mileage_at_repair != null && (
                    <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50">
                      <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Mileage</div>
                      <div className="font-mono text-sm">{Number(viewing.mileage_at_repair).toLocaleString()}</div>
                    </div>
                  )}
                  {viewing.engine_hours_at_repair != null && (
                    <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50">
                      <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Engine hrs</div>
                      <div className="font-mono text-sm">{viewing.engine_hours_at_repair}</div>
                    </div>
                  )}
                  {viewing.shop_invoice_number && (
                    <div className="border border-slate-200 rounded-sm p-2.5 bg-slate-50">
                      <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Invoice #</div>
                      <div className="font-mono text-sm">{viewing.shop_invoice_number}</div>
                    </div>
                  )}
                </div>

                {viewing.description && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-1">Description</div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap bg-white border border-slate-200 rounded-sm p-3">{viewing.description}</div>
                  </div>
                )}

                {viewing.work_performed && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-1">Work performed</div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap bg-white border border-slate-200 rounded-sm p-3">{viewing.work_performed}</div>
                  </div>
                )}

                {(viewing.parts_used || []).length > 0 && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-1">Parts used from inventory ({viewing.parts_used.length})</div>
                    <div className="border border-slate-200 rounded-sm divide-y divide-slate-200 text-xs bg-white">
                      {viewing.parts_used.map((l, i) => {
                        const p = partsMap.get(l.part_id);
                        return (
                          <div key={i} className="p-2 grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-6 font-mono">{p?.part_number || l.part_number || "—"} · <span className="font-semibold">{p?.name || l.name || "part"}</span></div>
                            <div className="col-span-2 text-right font-mono">×{l.quantity || 1}</div>
                            <div className="col-span-2 text-right font-mono">${Number(l.unit_cost || 0).toFixed(2)}</div>
                            <div className="col-span-2 text-right font-mono font-bold">${Number(l.line_total || (l.quantity || 1) * (l.unit_cost || 0)).toFixed(2)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(viewing.parts_purchased || []).length > 0 && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-1">Parts bought for this repair ({viewing.parts_purchased.length})</div>
                    <div className="border border-slate-200 rounded-sm divide-y divide-slate-200 text-xs bg-white">
                      {viewing.parts_purchased.map((l, i) => (
                        <div key={i} className="p-2 grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6 font-mono">{l.part_number || "—"} · <span className="font-semibold">{l.name || "part"}</span></div>
                          <div className="col-span-2 text-right font-mono">Bought ×{l.bought_quantity || 1} · Used ×{l.used_quantity || 0}</div>
                          <div className="col-span-2 text-right font-mono">${Number(l.unit_cost || 0).toFixed(2)}</div>
                          <div className="col-span-2 text-right font-mono font-bold">${Number((l.unit_cost || 0) * (l.bought_quantity || 1)).toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-slate-200 pt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Parts cost</div>
                    <div className="font-mono tabular-nums text-lg">${Number(viewing.parts_cost || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Labor cost</div>
                    <div className="font-mono tabular-nums text-lg">${Number(viewing.labor_cost || 0).toLocaleString()}</div>
                  </div>
                  {isOutside && (
                    <div>
                      <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Shop bill</div>
                      <div className="font-mono tabular-nums text-lg">${Number(viewing.shop_bill || 0).toLocaleString()}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold">Total</div>
                    <div className="font-mono tabular-nums text-2xl font-black text-blue-700" data-testid="repair-detail-total">
                      <span className={`text-xs mr-1 ${(viewing.currency === "USD") ? "text-emerald-700" : "text-slate-500"}`}>{viewing.currency === "USD" ? "US$" : "CA$"}</span>
                      {Number(viewing.total_cost || 0).toLocaleString()}
                    </div>
                  </div>
                </div>

                {(viewing.attachments || []).length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-1">Attachments ({viewing.attachments.length})</div>
                    <div className="flex flex-wrap gap-2">
                      {viewing.attachments.map((a, i) => (
                        <a key={i} href={a.url || a} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-300 rounded-sm text-xs hover:bg-slate-50 font-mono" data-testid={`repair-attachment-${i}`}>
                          <PaperclipIcon size={12} weight="bold" />
                          {a.filename || a.name || `file-${i + 1}`}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <DialogFooter className="mt-6">
                  <Button variant="outline" onClick={() => setViewing(null)} className="rounded-sm">Close</Button>
                  <Button onClick={() => { const r = viewing; setViewing(null); openEdit(r); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="repair-detail-edit">Edit repair</Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
